import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { AccordionRowState, cleanupOffscreen, closeComplete, loadComplete, resetAll, selectAllExpandedRows, toggleStart } from '@reducers/v2/tableAccordion/tableAccordion.slice';

export interface UseManageAccordionReturn {
  expandedRows: Record<string | number, AccordionRowState<unknown>>;
  toggleAccordion: (rowKey: string | number) => void;
  loadComplete: <TData>(rowKey: string | number, data: TData) => void;
  closeComplete: (rowKey: string | number) => void;
  cleanupOffscreen: (rowKey: string | number) => void;
  resetAll: () => void;
}

export const useManageAccordion = (): UseManageAccordionReturn => {
  const dispatch = useAppDispatch();
  const expandedRows = useSelector(selectAllExpandedRows);

  const toggleAccordion = useCallback(
    (rowKey: string | number) => {
      dispatch(toggleStart({ rowKey }));
    },
    [dispatch]
  );

  const handleLoadComplete = useCallback(
    <TData,>(rowKey: string | number, data: TData) => {
      dispatch(loadComplete({ rowKey, data }));
    },
    [dispatch]
  );

  const handleCloseComplete = useCallback(
    (rowKey: string | number) => {
      dispatch(closeComplete({ rowKey }));
    },
    [dispatch]
  );

  const handleCleanupOffscreen = useCallback(
    (rowKey: string | number) => {
      dispatch(cleanupOffscreen({ rowKey }));
    },
    [dispatch]
  );

  const handleResetAll = useCallback(() => {
    dispatch(resetAll());
  }, [dispatch]);

  return {
    expandedRows,
    toggleAccordion,
    loadComplete: handleLoadComplete,
    closeComplete: handleCloseComplete,
    cleanupOffscreen: handleCleanupOffscreen,
    resetAll: handleResetAll,
  };
};
